import { computed, type FunctionalComponent, shallowRef, type ShallowRef, isRef, reactive, type ComputedRef } from 'vue'
import type { UseDataTable } from './useDataTable'
import CheckboxInput from 'components/form/checkbox-input'
import { type DataTableColumn, type DataTableRowInternal } from '../types'
import { uref } from '@/utils/reactive/uref'
import { isShallowRef } from '@/utils/reactive/deepref'
import { useKeyboard } from '@/utils/hooks/useKeyboard'

export const useTransformedRows = (props: { table: UseDataTable, loading: ComputedRef<boolean> }) => {
  const selectionEnabled = computed(
    () =>
      !!props.table.options.selection?.action &&
      unref(!!props.table.options.rows?.selectable) &&
      props.table.rows &&
      uref(props.table.rows).length > 0
  )


  const selectionState = reactive<{
    prev: number
  }>({
    prev: -1
  });

  const kb = useKeyboard();

  const handleSelectAll = () => {
    props.table.state.setAllSelected(!props.table.state.allSelected.value)
  }

  const setSelected = (id: string, fun: (old: boolean) => boolean) => {
    const state = props.table.state.rows.value[id];
    state.value.selected = fun(state.value.selected === true);
  }

  const rowIds = computed(() => props.table.rows.value.map(it => it.value.id));

  const handleSelectOne = (id: string) => {
    const index = rowIds.value.indexOf(id);

    if (selectionState.prev >= 0 && kb.down('Shift')) {
      const start =  Math.min(index, selectionState.prev);
      const end = Math.max(index, selectionState.prev);
      const ids = rowIds.value.filter((it, i) => it === id || (i > start && i < end));
      ids.forEach((id) => setSelected(id, s => !s));
    } else {
      setSelected(id, (s) => !s);
    }
    selectionState.prev = index;
  }

  const headRows = computed((): DataTableRowInternal[] => {
    if (!unref(props.table.options.rows?.selectable)) return props.table.headRows.value || []
    return (props.table.headRows.value || []).map((row) => {
      const columns: ShallowRef<DataTableColumn>[] = [
        {
          label: '',
          width: 1,
          mobileWidth: '1px',
          body: () => {
            return selectionEnabled.value ? (
              <div
                style={{ width: 'fit-content' }}
                class="max-w-16 flex items-center justify-center align-center"
              >
                <CheckboxInput
                  variant="gray300"
                  hover
                  onClick={handleSelectAll}
                  value={props.table.state.allSelected.value}
                />
              </div>
            ) : (
              <div />
            )
          }
        },
        ...row.columns
      ].map((col) => (isRef(col) ? col : shallowRef(col)))

      const action = props.table.options.selection?.action
      if (action && columns.length > 1) {
        const InnerContent: FunctionalComponent<any> =
          props.table.state.selectionCount.value > 0
            ? () => <action table={props.table} />
            : () => uref(columns[columns.length - 1]).label || ''
        const last: DataTableColumn = {
          ...columns[columns.length - 1].value,
          body: () => (
            <div
              style={{
                maxWidth: '0px',
                ...(props.table.state.selectionCount.value > 0
                  ? {
                      textTransform: 'none'
                    }
                  : {})
              }}
            >
              <InnerContent />
            </div>
          )
        }
        columns[columns.length - 1].value = last
      }
      return {
        ...row,
        columns
      }
    })
  })

  const rows = computed((): ShallowRef<DataTableRowInternal>[] => {
    if (!unref(props.table.options.rows?.selectable)) return props.table.rows.value || []
    return (props.table.rows.value || [])
      .map((row, _i): DataTableRowInternal => {
        return {
          ...uref(row),
          selected: props.table.state.rows.value[uref(row).id]?.value?.selected ?? false, 
          columns: [
            shallowRef<DataTableColumn>({
              label: '',
              width: 1,
              mobileWidth: '1px',
              body: () => {
                const rw = uref(row)
                const state = props.table.state.rows.value[rw.id]
                return (
                  <div
                    style={{ width: 'fit-content' }}
                    class="max-w-16 flex items-center justify-center align-center"
                  >
                    <CheckboxInput
                      onClick={() => {
                        const state = props.table.state.rows.value[rw.id]
                        if (!state) return
                        handleSelectOne(rw.id);
                      }}
                      variant="gray300"
                      hover
                      value={!!state?.value?.selected}
                    />
                  </div>
                )
              }
            }),
            ...row.value.columns
          ].map((col) => (isRef(col) ? col : shallowRef<DataTableColumn>(col)))
        }
      })
      .map((it) =>
        isShallowRef<DataTableRowInternal>(it) ? it : shallowRef<DataTableRowInternal>(it)
      )
  });

  return { rows, headRows }
}
